import { type Common } from './common.js';
import { type EhrInformation } from './models/ehrInformation.js';
import type * as PatientNotification from './models/patient/notification.js';
import {
  type BasePatient,
  type BasePatientCombined,
  type ContactStatus,
  type PhoneLineType,
  type PhoneNumberStatus,
  type Sex,
} from './models/patient.js';
import { type PrevisitPharmacy } from './models/pharmacy.js';
import { type Language } from './models/previsit/language.js';

export class API {
  common: Common;
  baseEndpoint: string;

  constructor(common: Common) {
    this.common = common;
    this.baseEndpoint = '/patients';
  }

  async findOneByEhrInformationId(
    ehrInformationId: string
  ): Promise<{ patient: BasePatientCombined; ehrInformation: EhrInformation }> {
    const url = this.common.constructPath(`/ehr-information/${ehrInformationId}/patient`);
    return await this.common.getJSON(url);
  }

  async findOneByEhrValue(
    practiceId: string,
    value: string
  ): Promise<{ patient: BasePatient; ehrInformation: EhrInformation }> {
    const url = this.common.constructPath(`/ehr-information/patient`, {
      practiceId,
      value,
    });
    return await this.common.getJSON(url);
  }

  async create(patient: {
    firstName: string;
    lastName: string;
    dateOfBirth: Date;
    phoneNumber?: string;
    cellPhoneNumber?: string;
    homePhoneNumber?: string;
    workPhoneNumber?: string;
    sex?: Sex;
    ssn4?: string;
    email?: string;
    preferredLanguage?: Language;
    practiceId: string;
    patientEhrId: string;
    isTest?: boolean;
  }): Promise<
    BasePatient & {
      ehrInformationId: string;
      practiceId: string;
      patientEhrId: string;
    }
  > {
    const url = this.common.constructPath(`${this.baseEndpoint}/`);
    return await this.common.postJSON(url, patient);
  }

  async update(
    ehrInformationId: string,
    values: { dateOfBirth?: Date; firstName?: string; lastName?: string }
  ) {
    const url = this.common.constructPath(`/ehr-information/${ehrInformationId}`);
    return await this.common.patchJSON(url, values);
  }

  async findPatientPharmacies(): Promise<PrevisitPharmacy[]> {
    const url = this.common.constructPath(`/patient-previsit/patient-pharmacies`);
    return await this.common.getJSON(url);
  }

  async findContactStatusByEhrInformationId(ehrInformationId: string): Promise<ContactStatus> {
    const url = this.common.constructPath(`/ehr-information/${ehrInformationId}/contact-status`);
    return await this.common.getJSON(url);
  }

  async findStatusForPhoneNumber(number: string): Promise<{
    number: string;
    status: PhoneNumberStatus;
    type: PhoneLineType | null;
  }> {
    const url = this.common.constructPath('/patients/phone-number-status', {
      number,
    });
    return await this.common.getJSON(url);
  }

  async upsertStatusForPhoneNumber(
    number: string,
    status: PhoneNumberStatus
  ): Promise<{
    number: string;
    status: PhoneNumberStatus;
    type: PhoneLineType | null;
  }> {
    const url = this.common.constructPath('/patients/phone-number-status');
    return await this.common.postJSON(url, { number, status });
  }

  async findStatusForEmail(email: string): Promise<{ email: string; subscribed: boolean }> {
    const url = this.common.constructPath('/patients/email-status', { email });
    return await this.common.getJSON(url);
  }

  async upsertStatusForEmail(email: string, subscribed: boolean): Promise<{ success: boolean }> {
    const url = this.common.constructPath('/patients/email-status');
    return await this.common.postJSON(url, { email, subscribed });
  }

  async fetchPatientNotifications(args: {
    ehrInformationId: string;
    encounterId?: string;
    previsitFormSetId?: string;
    source: PatientNotification.Source;
    includeContent?: boolean;
  }): Promise<PatientNotification.PatientNotification[]> {
    const { ehrInformationId, encounterId, previsitFormSetId, source, includeContent } = args;
    const url = this.common.constructPath(`/ehr-information/${ehrInformationId}/notifications`, {
      encounterId,
      previsitFormSetId,
      source,
      includeContent,
    });
    return await this.common.getJSON(url);
  }
}
